import { motion } from "framer-motion";
import { Reveal } from "./shared";
import { fmtPct } from "./simulatorMath";

const X = 360;
const W = 264;
const LEFT = X - W / 2;
const H = 60;

export const fmtMil = (v) => {
  if (v == null || !isFinite(v)) return "—";
  const sign = v < 0 ? "−" : "";
  const a = Math.abs(v);
  return a >= 1000
    ? `${sign}R$ ${(a / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} mil`
    : `${sign}R$ ${Math.round(a)}`;
};

const NodeBox = ({ y, title, sub, kind = "proc", tone = "blue", delay = 0, testId, dim = 1 }) => {
  const strokes = { blue: "rgba(125,211,252,0.35)", glow: "#2E7CF6", green: "#10B981" };
  const fills = { blue: "#0C1830", glow: "#0E1D3D", green: "#0A1E1A" };
  const stroke = strokes[tone] || strokes.blue;
  return (
    <motion.g
      data-testid={testId}
      initial={{ opacity: 0, scale: 0.92 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      style={{ opacity: dim, transition: "opacity 0.5s" }}
    >
      <rect
        x={LEFT} y={y} width={W} height={H}
        rx={kind === "term" ? 30 : 14}
        fill={fills[tone] || fills.blue}
        stroke={stroke} strokeWidth={tone === "glow" ? 2 : 1.5}
        style={tone === "glow" ? { filter: "drop-shadow(0 0 14px rgba(46,124,246,0.45))" } : tone === "green" ? { filter: "drop-shadow(0 0 12px rgba(16,185,129,0.3))" } : undefined}
      />
      <text x={X} y={y + (sub ? 26 : 36)} textAnchor="middle" fill="#FFFFFF" fontSize={15} fontWeight={600} fontFamily="Manrope, sans-serif">
        {title}
      </text>
      {sub && (
        <text x={X} y={y + 45} textAnchor="middle" fill="#9FB3D1" fontSize={10.5} fontFamily="'JetBrains Mono', monospace">
          {sub}
        </text>
      )}
    </motion.g>
  );
};

const Arrow = ({ d, color = "#2E7CF6", marker = "arr-blue", delay = 0, dim = 1 }) => (
  <motion.path
    d={d}
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    markerEnd={`url(#${marker})`}
    initial={{ pathLength: 0 }}
    whileInView={{ pathLength: 1 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, delay, ease: "easeOut" }}
    style={{ opacity: dim, transition: "opacity 0.5s" }}
  />
);

const Pulse = ({ d, color = "#7DD3FC", dur = "1.8s", begin = "-0.5s", r = 4 }) => (
  <circle r={r} fill={color} style={{ filter: `drop-shadow(0 0 6px ${color}) drop-shadow(0 0 14px ${color})` }}>
    <animateMotion dur={dur} begin={begin} repeatCount="indefinite" path={d} />
  </circle>
);

const Note = ({ x, y, w, h, title, lines, linkTo, delay = 0, testId }) => (
  <motion.g
    data-testid={testId}
    initial={{ opacity: 0, x: 24 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    <rect x={x} y={y} width={w} height={h} rx={10} fill="rgba(255,255,255,0.03)" stroke="rgba(125,211,252,0.35)" strokeWidth={1.2} strokeDasharray="5 4" />
    <polygon points={`${x + w - 18},${y} ${x + w},${y} ${x + w},${y + 18}`} fill="#0C1830" stroke="rgba(125,211,252,0.35)" strokeWidth={1.2} />
    <text x={x + 14} y={y + 24} fill="#7DD3FC" fontSize={10} fontWeight={700} fontFamily="'JetBrains Mono', monospace" letterSpacing={1.5}>
      {title}
    </text>
    {lines.map((l, i) => (
      <text key={i} x={x + 14} y={y + 44 + i * 16} fill="#9FB3D1" fontSize={11.5} fontFamily="Manrope, sans-serif">
        {l}
      </text>
    ))}
    {linkTo && (
      <motion.path
        d={linkTo}
        fill="none"
        stroke="rgba(125,211,252,0.4)"
        strokeWidth={1.2}
        strokeDasharray="4 4"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: delay + 0.3 }}
      />
    )}
  </motion.g>
);

export const MethodFlow = ({ sim }) => {
  const cobriu = sim ? sim.status === "acima" || sim.status === "equilibrio" : false;
  const dimNao = sim ? (cobriu ? 0.12 : 1) : 1;
  const dimSim = sim ? (cobriu ? 1 : 0.12) : 1;
  const n1Sub = sim ? `custo do lote: ${fmtMil(sim.custo)}` : "custo de aquisição registrado";
  const n7Sub = sim ? `margem de contribuição: ${fmtPct(sim.imcPct)}` : "o que sobra para cobrir a operação";
  const n8Sub = sim ? (sim.pe != null ? `${fmtMil(sim.pe)} de faturamento` : "não calculável") : "operação sustentada pelo faturamento";
  const n9Sub = sim
    ? sim.pe != null
      ? cobriu
        ? `resultado: ${fmtMil(sim.resultado)}`
        : `faltam ${fmtMil(sim.falta)}`
      : "revise as premissas"
    : "o lucro real do negócio";

  return (
    <Reveal>
      <div data-testid="method-flow-diagram" className="relative rounded-3xl border border-white/[0.07] bg-gradient-to-b from-white/[0.03] to-transparent p-4 sm:p-8">
        <div className="overflow-x-auto">
          <div className="mx-auto min-w-[680px] max-w-[760px]">
            <svg viewBox="0 0 720 1000" className="h-auto w-full" role="img" aria-label="Fluxograma do método TRIA">
              <defs>
                <marker id="arr-blue" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto">
                  <path d="M0,0 L9,4.5 L0,9 z" fill="#2E7CF6" />
                </marker>
                <marker id="arr-amber" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto">
                  <path d="M0,0 L9,4.5 L0,9 z" fill="#F59E0B" />
                </marker>
                <marker id="arr-green" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto">
                  <path d="M0,0 L9,4.5 L0,9 z" fill="#10B981" />
                </marker>
              </defs>

              {/* início */}
              <motion.circle cx={X} cy={30} r={9} fill="#7DD3FC" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.4 }} />
              <Arrow d={`M${X} 39 V58`} delay={0.1} />

              <NodeBox y={58} title="Compra do lote" sub={n1Sub} kind="term" delay={0.15} testId="flow-node-1" />
              <Arrow d={`M${X} 118 V138`} delay={0.3} />
              <NodeBox y={138} title="Formação do custo" sub="custos diretos + despesas do lote" delay={0.4} testId="flow-node-2" />
              <Arrow d={`M${X} 198 V218`} delay={0.5} />
              <NodeBox y={218} title="Definição de preços" sub="preço inicial por peça" delay={0.6} testId="flow-node-3" />
              <Arrow d={`M${X} 278 V298`} delay={0.7} />
              <NodeBox y={298} title="Remarcações" sub="margem recalculada a cada mudança" tone="glow" delay={0.8} testId="flow-node-4" />
              <Arrow d={`M${X} 358 V378`} delay={0.9} />
              <NodeBox y={378} title="Vendas" sub="registro em tempo real" delay={1.0} testId="flow-node-5" />
              <Arrow d={`M${X} 438 V458`} delay={1.1} />
              <NodeBox y={458} title="Receita realizada" sub="projetado × realizado × potencial" tone="glow" delay={1.2} testId="flow-node-6" />
              <Arrow d={`M${X} 518 V538`} delay={1.3} />
              <NodeBox y={538} title="Margem de contribuição" sub={n7Sub} delay={1.4} testId="flow-node-7" />
              <Arrow d={`M${X} 598 V626`} delay={1.5} />

              {/* decisão */}
              <motion.g
                data-testid="flow-decision"
                initial={{ opacity: 0, scale: 0.85 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
              >
                <polygon points={`${X},626 494,694 ${X},762 226,694`} fill="#1A1608" stroke="#F59E0B" strokeWidth={1.8} style={{ filter: "drop-shadow(0 0 14px rgba(245,158,11,0.25))" }}>
                  <animate attributeName="stroke-opacity" values="1;0.35;1" dur="2.4s" repeatCount="indefinite" />
                </polygon>
                <text x={X} y={688} textAnchor="middle" fill="#FFFFFF" fontSize={13.5} fontWeight={600} fontFamily="Manrope, sans-serif">Cobriu os</text>
                <text x={X} y={706} textAnchor="middle" fill="#FFFFFF" fontSize={13.5} fontWeight={600} fontFamily="Manrope, sans-serif">custos fixos?</text>
              </motion.g>

              {/* loop NÃO */}
              <text x={126} y={684} fill="#F59E0B" fontSize={11} fontFamily="'JetBrains Mono', monospace" style={{ opacity: dimNao, transition: "opacity 0.5s" }}>Não · ajustar preços e metas</text>
              <Arrow d={`M226 694 H120 V328 H222`} color="#F59E0B" marker="arr-amber" delay={1.9} dim={dimNao} />

              {/* caminho SIM */}
              <text x={376} y={782} fill="#10B981" fontSize={11.5} fontFamily="'JetBrains Mono', monospace" style={{ opacity: dimSim, transition: "opacity 0.5s" }}>Sim</text>
              <Arrow d={`M${X} 762 V786`} color="#10B981" marker="arr-green" delay={2.0} dim={dimSim} />
              <g style={{ opacity: dimSim, transition: "opacity 0.5s" }}>
                <NodeBox y={786} title="Ponto de equilíbrio" sub={n8Sub} kind="term" tone="green" delay={2.1} testId="flow-node-8" />
                <Arrow d={`M${X} 846 V866`} color="#10B981" marker="arr-green" delay={2.25} />
                <NodeBox y={866} title="Resultado operacional" sub={n9Sub} kind="term" tone="green" delay={2.35} testId="flow-node-9" />
                <Arrow d={`M${X} 926 V942`} color="#10B981" marker="arr-green" delay={2.5} />
                <motion.g initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 2.6 }}>
                  <circle cx={X} cy={954} r={11} fill="none" stroke="#10B981" strokeWidth={2} />
                  <circle cx={X} cy={954} r={5} fill="#10B981" />
                </motion.g>
              </g>

              {/* pulsos de dados percorrendo o fluxo em tempo real */}
              <g data-testid="flow-pulses">
                <Pulse d={`M${X} 39 V58`} begin="-0.2s" />
                <Pulse d={`M${X} 118 V138`} begin="-0.7s" />
                <Pulse d={`M${X} 198 V218`} begin="-1.1s" />
                <Pulse d={`M${X} 278 V298`} begin="-1.5s" />
                <Pulse d={`M${X} 358 V378`} begin="-0.4s" />
                <Pulse d={`M${X} 438 V458`} begin="-0.9s" />
                <Pulse d={`M${X} 518 V538`} begin="-1.3s" />
                <Pulse d={`M${X} 598 V626`} dur="1.5s" begin="-0.6s" />
              </g>
              {/* ciclo de ajuste (Não) */}
              <g data-testid="flow-pulses-nao" style={{ opacity: dimNao, transition: "opacity 0.5s" }}>
                <Pulse d={`M226 694 H120 V328 H222`} color="#F59E0B" dur="3.2s" begin="-0.8s" r={4.5} />
                <Pulse d={`M226 694 H120 V328 H222`} color="#F59E0B" dur="3.2s" begin="-2.4s" r={4.5} />
              </g>
              {/* caminho do lucro (Sim) */}
              <g data-testid="flow-pulses-sim" style={{ opacity: dimSim, transition: "opacity 0.5s" }}>
                <Pulse d={`M${X} 762 V786`} color="#34D399" dur="1.5s" begin="-0.3s" />
                <Pulse d={`M${X} 846 V866`} color="#34D399" dur="1.6s" begin="-1.0s" />
                <Pulse d={`M${X} 926 V942`} color="#34D399" dur="1.6s" begin="-1.6s" />
              </g>

              {/* notas UML com os diferenciais */}
              <Note
                x={520} y={292} w={186} h={100}
                title="DIFERENCIAL TRIA"
                lines={["Margem em atualização", "contínua — a cada", "remarcação e venda,", "sem esperar o mês fechar"]}
                linkTo="M520 336 L496 328"
                delay={1.0}
                testId="flow-note-margem"
              />
              <Note
                x={520} y={452} w={186} h={88}
                title="DIFERENCIAL TRIA"
                lines={["Três réguas separadas:", "projetado, realizado", "e potencial do lote"]}
                linkTo="M520 490 L496 488"
                delay={1.4}
                testId="flow-note-reguas"
              />
            </svg>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-white/[0.06] pt-5" data-testid="flow-legend">
          <span className="flex items-center gap-2 text-[11.5px] text-tria-mist"><span className="h-2.5 w-2.5 rounded-[4px] border border-tria-sky/50 bg-tria-card" />Etapa do fluxo</span>
          <span className="flex items-center gap-2 text-[11.5px] text-tria-mist"><span className="h-2.5 w-2.5 rotate-45 border border-amber-400/70 bg-[#1A1608]" />Decisão</span>
          <span className="flex items-center gap-2 text-[11.5px] text-tria-mist"><span className="h-[2px] w-5 bg-amber-400" />Ciclo de ajuste</span>
          <span className="flex items-center gap-2 text-[11.5px] text-tria-mist"><span className="h-[2px] w-5 bg-emerald-400" />Caminho do lucro</span>
        </div>
      </div>
    </Reveal>
  );
};
